import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useLocation } from "react-router-dom";
import Box from "utils/Box";
import Grid from "utils/Grid";
import { Typography, Button, Card } from "@mui/material";
import ContentHeader from "../../../common/ContentHeader";
import TableGenerator from "../../../../../../../Common/TableGenerator";
import Label from "../../../common/Label";
import { useNavigate } from "react-router-dom";

import styles from "./styles";
import classNames from "classnames";
import _ from "lodash";
import _flatten from "lodash/flatten";
import moment from "dayjs";
import { useSnackbar } from "notistack";
import { useProducts } from "components/CustomerAdmin/Apps/planogram/components/hooks";
import { useDispatch, useSelector } from "react-redux";
import { getKittinPlanData, removeKittingPlanData } from "redux/actions";
import { MSeriesModulesDropDown } from "components/Common";

const AddKittingPlan = forwardRef((props, ref) => {
  const {
    deviceDetails,
    operator,
    replenishmentTime,
    onButtonDisabledChange,
    onButtonLoad,
    onAddResponseData,
    onSave,
    onNext,
    onPrevious,
  } = props;

  const location = useLocation();
  const [dataCount] = useState(0);
  const [page, setPage] = useState(0);
  const [modulesSelected, setModulesSelected] = useState([]);
  const [mSeriesModules, setMSeriesModules] = useState([]);
  const [mSeriesSelectedModule, setMSeriesSelectedModule] = useState(null);
  const [currentDeviceSerial, setCurrentDeviceSerial] = useState("");
  const [moduleExpectedValues, setModuleExpectedValues] = useState({});
  const [moduleShelfValues, setModuleShelfValues] = useState({});
  const [isPeer, setIsPeer] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const expectedRestockValuesRef = useRef([]);
  const shelfLifeValuesRef = useRef([]);
  const [flagCount, setFlagCount] = useState(0);

  const current_user = useSelector((state) => state.userReducer.current_user);
  const kittingPlanData = useSelector(
    (state) => state.kittingPlan.kittingPlanData
  );
  const loader = useSelector((state) => state.kittingPlan.isLoading);

  const dispatch = useDispatch();

  const navigate = useNavigate();
  const classes = styles();

  const { productsData, fetchProducts } = useProducts();

  const { enqueueSnackbar } = useSnackbar();
  let flag = true;

  const fields = [
    {
      key: "rack_number",
      columnName: "Rack Number",
      type: "text",
      render: (_, x) => `${x.row}-${x.col}`,
      visible: true,
    },
    {
      key: "product_id",
      columnName: "Product Id",
      render: (_, val) => val.product_id || "---",
      type: "text",
      visible: true,
    },
    {
      key: "product_sku",
      columnName: "Product SKU",
      render: (fieldValue, val) =>
        fieldValue ||
        _.find(productsData, (x) => x.id === val.product_id)?.product_sku ||
        "---",
      type: "text",
      visible: true,
    },
    {
      key: "product_name",
      columnName: "Product Name",
      render: (fieldValue, val) =>
        fieldValue ||
        _.find(productsData, (x) => x.id === val.product_id)?.product_name ||
        "---",
      type: "text",
      visible: true,
    },
    {
      key: "stock",
      columnName: "Stock/Capacity",
      render: (_, value) => `${value.stock} / ${value.capacity}`,
      type: "text",
      visible: true,
    },
    {
      key: "expected_restock_value",
      columnName: "Expected Restock Value",
      type: "text",
      visible: true,

      render: (_, value) => (
        <input
          type="number"
          className="kitting-plan-restock-input-fields"
          disabled={!value.product_id}
          id={`expected-restock-${value?.row}-${value?.col}`}
          ref={(e) => {
            expectedRestockValuesRef.current[`${value?.row}-${value?.col}`] = e;
          }}
          min={`-${value.stock}`}
          max={
            value.stock > value.capacity
              ? 0
              : (value?.capacity || 0) - (value?.stock || 0)
          }
          data-max={
            value.stock > value.capacity
              ? 0
              : (value?.capacity || 0) - (value?.stock || 0)
          }
          data-min={`-${value.stock}`}
          defaultValue={
            value.expected_restock_value < 0
              ? 0
              : moduleExpectedValues[mSeriesSelectedModule]?.[
                  `${value?.row}-${value?.col}`
                ]
              ? moduleExpectedValues[mSeriesSelectedModule][
                  `${value?.row}-${value?.col}`
                ]
              : mSeriesSelectedModule
              ? value.expected_restock_value
              : expectedRestockValuesRef.current[`${value?.row}-${value?.col}`]
                  ?.value || value.expected_restock_value
          }
          onChange={() => onExpectedRestockValueChange(mSeriesSelectedModule)}
        />
      ),
    },

    {
      key: "shelf_life",
      columnName: "Shelf Life",
      type: "text",
      visible: true,
      render: (_, val) => val.shelf_life || "---",
    },
    {
      key: "inventory",
      columnName: "Sold-out Status",
      type: "text",
      visible: true,
      render: (_, value) => (
        <>
          {getInventoryLabel(value.stock, value.capacity, value?.isException)}
        </>
      ),
    },
  ];

  const getInventoryLabel = (inventory, capacity, isException) => {
    const stockPercentage = (100 * inventory) / capacity;
    let inventoryType = undefined;
    if (isException) {
      inventoryType = "exception";
    } else if (stockPercentage == 0 || capacity == 0) {
      inventoryType = "out_of_stock";
    } else if (stockPercentage <= 30) {
      inventoryType = "limited";
    } else {
      inventoryType = "in_stock";
    }
    const map = {
      in_stock: {
        text: "In Stock",
        color: "success",
      },
      out_of_stock: {
        text: "Out of Stock",
        color: "error",
      },
      limited: {
        text: "Limited",
        color: "warning",
      },
      exception: {
        text: "Exception",
        color: "error",
      },
    };

    const { text, color } = map[inventoryType];

    return <Label color={color}>{text}</Label>;
  };

  useEffect(() => {
    if (deviceDetails.is_m_series) {
      (deviceDetails?.modules?.length
        ? deviceDetails.modules
        : ["m-1"]
      ).forEach((element) => {
        dispatch(
          getKittinPlanData({
            oro_id: deviceDetails.oro_id,
            module_number: element,
            total_modules: deviceDetails.modules.length,
          })
        );
      });
    } else {
      dispatch(getKittinPlanData({ oro_id: deviceDetails.oro_id }));
    }
    fetchProducts();

    return () => {
      dispatch(removeKittingPlanData());
    };
  }, []);

  const onExpectedRestockValueChange = (isMSeries) => {
    if (isMSeries) {
      const expectedRestockValues = {};
      for (let key in expectedRestockValuesRef.current) {
        if (
          parseInt(expectedRestockValuesRef.current[key]?.value) >
            parseInt(expectedRestockValuesRef.current[key]?.dataset.max) ||
          parseInt(expectedRestockValuesRef.current[key]?.value) <
            parseInt(expectedRestockValuesRef.current[key]?.dataset.min)
        ) {
          alert('Invalid "Expected Restock Value" of rack ' + key);
          return;
        }
        expectedRestockValues[key] =
          expectedRestockValuesRef.current[key]?.value;
      }
      setModuleExpectedValues((prev) => ({
        ...prev,
        [mSeriesSelectedModule]: expectedRestockValues,
      }));
    }
  };

  // let flag = true
  const handleAddMainDevicePlan = async (
    restockValues,
    shelfLifeValues,
    module,
    stamp,
    count
  ) => {
    try {
      let kittingData = deviceDetails.is_m_series
        ? kittingPlanData[deviceDetails.serial_number][module]
        : kittingPlanData[deviceDetails.serial_number];

      if (!kittingData?.length) {
        if (count && count === deviceDetails?.modules?.length) {
          enqueueSnackbar("Kitting Plan added successfully");
        }
        return;
      }

      const kittingPlanDetails = JSON.stringify(
        _.map(kittingData, (x) => ({
          product_id: x.product_id,
          product_name: x.product_name,
          stock: Number(x.stock || 0),
          capacity: Number(x.capacity || 0),
          price: Number(x.price || 0),
          is_disabled: x.is_disabled || false,
          row: x.row,
          col: x.col,
          expected_restock_value:
            restockValues?.[`${x.row}-${x.col}`] || x.expected_restock_value,
          shelf_life:
            shelfLifeValues?.[`${x.row}-${x.col}`] || x.shelf_life || "",
          module_number: module,
        }))
      );

      const data = {
        device: deviceDetails.id,
        kittingPlanDetails,
        replenishment_time: moment(
          replenishmentTime,
          "YYYY-MM-DD HH:mm A"
        ).toISOString(),
      };
      if (deviceDetails.is_m_series) {
        data.module_number = module;
        data.parent_id = stamp;
      }

      if (operator) {
        data["assigned_to"] = operator;
      }

      if (current_user.type === "SA" && deviceDetails.company && !operator) {
        data["operator_id"] = deviceDetails.company?.id;
      }

      const response = await window.axiosIns.post("kittingplan", data);
      if (
        response.data.kittingPlanDetails &&
        deviceDetails.is_m_series &&
        count === 1
      ) {
        // have to work on this
        // flag = false
        try {
          // Parsing the JSON string to an array
          const kittingPlanDetailsArray = JSON.parse(
            response.data.kittingPlanDetails
          );

          // Map over the array and convert 'expected_restock_value' to an integer
          const transformedKittingPlanDetails = kittingPlanDetailsArray.map(
            (item) => ({
              ...item,
              expected_restock_value: parseInt(item.expected_restock_value, 10),
            })
          );

          // Create a new response object with the transformed array
          const resToSend = {
            ...response.data,
            kittingPlanDetails: transformedKittingPlanDetails,
          };

          onAddResponseData(resToSend);
        } catch (error) {
          console.error("Error parsing kittingPlanDetails:", error);
          // Return the original response if parsing fails
          onAddResponseData(response.data);
        }
      } else if (!deviceDetails.is_m_series) {
        try {
          // Parsing the JSON string to an array
          const kittingPlanDetailsArray = JSON.parse(
            response.data.kittingPlanDetails
          );

          // Map over the array and convert 'expected_restock_value' to an integer
          const transformedKittingPlanDetails = kittingPlanDetailsArray.map(
            (item) => ({
              ...item,
              expected_restock_value: parseInt(item.expected_restock_value, 10),
            })
          );

          // Create a new response object with the transformed array
          const resToSend = {
            ...response.data,
            kittingPlanDetails: transformedKittingPlanDetails,
          };

          onAddResponseData(resToSend);
        } catch (error) {
          console.error("Error parsing kittingPlanDetails:", error);
          // Return the original response if parsing fails
          onAddResponseData(response.data);
        }
      }

      if (count) {
        if (count === deviceDetails?.modules?.length) {
          enqueueSnackbar("Kitting Plan added successfully");
        }
      } else {
        enqueueSnackbar("Kitting Plan added successfully");
      }
      dispatch(removeKittingPlanData());

      // navigate("/inventory?tab=kitting-plan");
    } catch (err) {
      console.log(err);
    }
  };
  const handleAddPeerDevicePlan = async (
    restockValues,
    shelfLifeValues,
    module,
    stamp = null
  ) => {
    try {
      const data = {
        peer_devices: [],
      };
      let kittingData = deviceDetails.is_m_series
        ? kittingPlanData[currentDeviceSerial][mSeriesSelectedModule]
        : kittingPlanData[currentDeviceSerial];

      const kittingPlanDetails = JSON.stringify(
        _.map(kittingData, (x) => ({
          product_id: x.product_id,
          product_name: x.product_name,
          stock: Number(x.stock || 0),
          capacity: Number(x.capacity || 0),
          price: Number(x.price || 0),
          row: x.row,
          col: x.col,
          expected_restock_value:
            restockValues?.[`${x.row}-${x.col}`] || x.expected_restock_value,
          shelf_life:
            shelfLifeValues?.[`${x.row}-${x.col}`] || x.shelf_life || "",
          module_number: mSeriesSelectedModule,
        }))
      );

      data.peer_devices.push({
        peer_serial_number: currentDeviceSerial,
        data: {
          kittingPlanDetails,
          replenishment_time: moment(
            replenishmentTime,
            "YYYY-MM-DD HH:mm A"
          ).toISOString(),
          device: deviceDetails?.id,
          assigned_to: deviceDetails?.assigned_to,
          module_number: module,
          parent_id: stamp,
        },
      });

      const response = await window.axiosIns.post(
        `kittingplan/peer?device_id=${deviceDetails.id}`,
        data
      );
      if (
        response.data.kittingPlanDetails &&
        deviceDetails.is_m_series &&
        flag
      ) {
        // have to work on this
        flag = false;
        try {
          const kittingPlanDetailsArray = JSON.parse(
            response.data.kittingPlanDetails
          );
          const resToSend = {
            ...response.data,
            kittingPlanDetails: kittingPlanDetailsArray,
          };

          onAddResponseData(resToSend);
        } catch (error) {
          console.error("Error parsing kittingPlanDetails:", error);
          // Return the original response if parsing fails
          onAddResponseData(response.data);
        }
      } else if (!deviceDetails.is_m_series) {
        try {
          const kittingPlanDetailsArray = JSON.parse(
            response.data.kittingPlanDetails
          );
          const resToSend = {
            ...response.data,
            kittingPlanDetails: kittingPlanDetailsArray,
          };

          onAddResponseData(resToSend);
        } catch (error) {
          console.error("Error parsing kittingPlanDetails:", error);
          // Return the original response if parsing fails
          onAddResponseData(response.data);
        }
      }

      enqueueSnackbar("Kitting Plan added successfully");
      dispatch(removeKittingPlanData());

      // navigate("/inventory?tab=kitting-plan");
    } catch (err) {}
  };
  useEffect(() => {
    setCurrentDeviceSerial(
      deviceDetails.peer_device || deviceDetails.serial_number || ""
    );
    if (deviceDetails?.is_m_series) {
      let modulesObject = _.map(deviceDetails?.modules, (item, index) => {
        return { label: `Module ${index + 1}`, value: item };
      });
      setMSeriesModules(modulesObject);
      setMSeriesSelectedModule(deviceDetails?.modules[0]);
      setLoading(false);
    }
    if (deviceDetails.peer_device) {
      setIsPeer(true);
    }
  }, [location]);

  const handleAdd = (expectedRestockValues, shelfLifeValues) => {
    setIsMutating(true); // Set loading state at the beginning

    const timeStamp = Date.now();
    const promises = []; // To collect all promises from async calls

    if (isPeer) {
      if (deviceDetails.is_m_series) {
        deviceDetails.modules.forEach((module) => {
          const promise = handleAddPeerDevicePlan(
            module === mSeriesSelectedModule
              ? expectedRestockValues
              : moduleExpectedValues[module],
            shelfLifeValues,
            module,
            timeStamp
          );
          promises.push(promise);
        });
      } else {
        promises.push(
          handleAddPeerDevicePlan(expectedRestockValues, shelfLifeValues)
        );
      }
    } else {
      if (deviceDetails.is_m_series) {
        deviceDetails.modules.forEach((module, index) => {
          const promise = handleAddMainDevicePlan(
            module === mSeriesSelectedModule
              ? expectedRestockValues
              : moduleExpectedValues[module],
            shelfLifeValues,
            module,
            timeStamp,
            index + 1
          );
          promises.push(promise);
        });
      } else {
        promises.push(
          handleAddMainDevicePlan(expectedRestockValues, shelfLifeValues)
        );
      }
    }

    return Promise.all(promises)
      .then(() => {
        setIsMutating(false); // Clear loading state after all promises resolve
        // enqueueSnackbar("Kitting Plans added successfully", { variant: "success" });
      })
      .catch((err) => {
        console.error("Error in saving Kitting Plans:", err);
        enqueueSnackbar("Failed to add Kitting Plans.", { variant: "error" });
        setIsMutating(false);
      });
  };
  const handleSave = async () => {
    const expectedRestockValues = {};
    const shelfLifeValues = {};
    let validationErrors = [];

    for (let key in expectedRestockValuesRef.current) {
      const inputValue = parseInt(
        expectedRestockValuesRef.current[key]?.value,
        10
      );
      const maxValid = parseInt(
        expectedRestockValuesRef.current[key]?.dataset.max,
        10
      );
      const minValid = parseInt(
        expectedRestockValuesRef.current[key]?.dataset.min,
        10
      );

      if (inputValue > maxValid) {
        validationErrors.push(
          `Invalid "Expected Restock Value" of rack ${key}: Input ${inputValue} is out of range, value must be less than or equal to ${maxValid}.`
        );
      } else if (inputValue < minValid) {
        validationErrors.push(
          `Invalid "Expected Restock Value" of rack ${key}: Input ${inputValue} is out of range ,value must be greater than or equal to ${minValid}.`
        );
      } else {
        expectedRestockValues[key] =
          expectedRestockValuesRef.current[key]?.value;
        shelfLifeValues[key] = shelfLifeValuesRef.current[key]?.value;
      }
    }

    if (validationErrors.length > 0) {
      // Join all error messages into a single alert or throw a combined error
      // alert(validationErrors.join("\n"));
      throw new Error(validationErrors.join("\n"));
    }

    try {
      await handleAdd(expectedRestockValues, shelfLifeValues);
    } catch (err) {
      console.error(err);
      throw err; // Propagate the error to be caught by the parent.
    }
  };

  const isButtonDisabled = React.useMemo(() => {
    let kittingData = deviceDetails.is_m_series
      ? kittingPlanData?.[currentDeviceSerial]?.[mSeriesSelectedModule]
      : kittingPlanData?.[currentDeviceSerial];
    return (
      isMutating ||
      loader ||
      !kittingData?.length ||
      _.every(kittingData, (val) => !Boolean(val.product_id))
    );
  }, [kittingPlanData, currentDeviceSerial, isMutating, loader]);

  const isButtonLoading = React.useMemo(() => {
    return isMutating || loader;
  }, [kittingPlanData, currentDeviceSerial, isMutating, loader]);

  useEffect(() => {
    onButtonLoad(isButtonLoading);
  }, [isButtonLoading]);

  useEffect(() => {
    onButtonDisabledChange(isButtonDisabled);
  }, [isButtonDisabled]);

  const onModuleChange = (val) => {
    const expectedRestockValues = {};
    const shelfLifeValues = {};

    for (let key in expectedRestockValuesRef.current) {
      if (
        parseInt(expectedRestockValuesRef.current[key]?.value) >
          parseInt(expectedRestockValuesRef.current[key]?.dataset.max) ||
        parseInt(expectedRestockValuesRef.current[key]?.value) <
          parseInt(expectedRestockValuesRef.current[key]?.dataset.min)
      ) {
        alert('Invalid "Expected Restock Value" of rack ' + key);
        return;
      }
      expectedRestockValues[key] = expectedRestockValuesRef.current[key]?.value;
      shelfLifeValues[key] = shelfLifeValuesRef.current[key]?.value;
    }
    setModuleExpectedValues((prev) => ({
      ...prev,
      [mSeriesSelectedModule]: expectedRestockValues,
    }));
    setModuleShelfValues((prev) => ({
      ...prev,
      [mSeriesSelectedModule]: shelfLifeValues,
    }));
    setMSeriesSelectedModule(val?.value);
    // dispatch(
    //   getKittinPlanData({
    //     oro_id: location.state.oro_id,
    //     module_number: val?.value,
    //   })
    // );
  };

  useImperativeHandle(ref, () => ({
    handleSave,
  }));

  return (
    <form onSubmit={handleSave}>
      <div id="sa-modules-wrapper" className={classes.wrapper}>
        {/* <ContentHeader title="Add Kitting Plan" /> */}
        <Card
          style={{ margin: "0px 20px 20px 20px" }}
          variant="outlined"
          // className={classes.headerToolbar}
        >
          <Grid
            style={{
              padding: "10px 20px",
              display: "flex",
              alignItems: "center",
            }}
            container
            spacing={1}
          >
            <Grid xs={12} sm={6} md={6} lg={3}>
              <Box display="flex" alignItems="center">
                <Typography style={{ marginRight: 10 }} variant="subtitle2">
                  <strong>VM Serial : </strong>
                  {currentDeviceSerial || "---"}
                </Typography>
              </Box>
            </Grid>
            <Grid xs={12} sm={6} md={6} lg={3}>
              <Typography variant="subtitle2">
                <strong>VM Name : </strong>
                {deviceDetails?.vm_name || "---"}
              </Typography>
            </Grid>
            <Grid xs={12} sm={6} md={6} lg={3}>
              <Typography variant="subtitle2">
                <strong>Operator : </strong>
                {deviceDetails?.operator_name ||
                  deviceDetails?.company?.business_name ||
                  "---"}
              </Typography>
            </Grid>
            {mSeriesSelectedModule && (
              <Grid
                
                xs={12}
                sm={6}
                md={6}
                lg={3}
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <Typography variant="subtitle2" style={{ marginRight: 5 }}>
                  <strong>Module :</strong>
                </Typography>
                <MSeriesModulesDropDown
                  onChange={(val) => onModuleChange(val)}
                  value={mSeriesSelectedModule}
                  options={mSeriesModules}
                  defaultValue={mSeriesModules[0]}
                  size="small"
                  isLoading={isLoading}
                />
              </Grid>
            )}
          </Grid>
        </Card>

        <div className={classes.toolbar}>
          {/* <div className={classes.crudButtons}>
            <Button
              variant="contained"
              outlined
              color="primary"
              type="submit"
              className={classNames(classes.crudButtons, "text-red")}
              disabled={isButtonDisabled}
            >
              Add Kitting Plan
            </Button>
            <Button
              className=" text-red"
              onClick={() => navigate("/inventory?tab=kitting-plan")}
              variant="outlined"
              color="primary"
            >
              Close
            </Button>
          </div> */}
          <div className="d-flex" />
        </div>

        <div className={classes.content}>
          <TableGenerator
            searchColumnsFilter={true}
            sensorTable={true}
            fields={_flatten(fields)}
            data={
              deviceDetails.is_m_series
                ? kittingPlanData?.[currentDeviceSerial]?.[
                    mSeriesSelectedModule
                  ]?.filter(
                    (item) =>
                      item?.is_disabled === undefined ||
                      item?.is_disabled === false
                  ) || []
                : kittingPlanData[currentDeviceSerial]?.filter(
                    (item) =>
                      item?.is_disabled === undefined ||
                      item?.is_disabled === false
                  ) || []
            }
            loader={loader}
            onChangePage={(page) => console.log(page)}
            backendPagination={true}
            currentPage={page}
            handleSortChange={(ordering) => {}}
            onPageChange={(page, direction) => {
              setPage(page);
            }}
            onRowPerPageChange={(rows) => {}}
            dataCount={dataCount}
            selectedRecords={modulesSelected}
            rowOnePage={2500}
            onChangeSelected={(modulesSelected) =>
              setModulesSelected(modulesSelected)
            }
            disablePagination={true}
          />
        </div>
      </div>
    </form>
  );
});

export default AddKittingPlan;
